import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DatePipe, NgStyle } from '@angular/common';
import moment, { Moment } from 'moment';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { StormMapContent } from '../../../../../shared/components/map-preview/model/map-content/StormMapContent';
import { StormInfo } from '../../../../../shared/models/storm.model';
import { StormMapDataService } from '../storm-map-data.service';

enum PlaybackAction {
  STOP = 'stop',
  PLAY = 'play',
}

enum PlaybackStatus {
  STOPPED = 'stopped',
  PLAYING = 'playing',
  LOADING = 'loading'
}

interface TimeAxisMarker {
  position: number;
  label: string;
  isStart?: boolean;
  isEnd?: boolean;
}

@Component({
  selector: 'app-storm-control',
  imports: [NgStyle, DatePipe, MatProgressSpinner, MatIcon, MatButton, MatMenuTrigger, MatMenu, MatMenuItem],
  templateUrl: './storm-control.component.html',
  styleUrl: './storm-control.component.scss',
})
export class StormControlComponent implements OnDestroy, AfterViewInit, OnChanges {
  @Input() time: number; // this can be decimal when animating shift progress
  @Input() stormMapContent: StormMapContent;
  @Input() storm: StormInfo;
  @Output() timeChanged = new EventEmitter<number>();
  @Output() speedChanged = new EventEmitter<number>();

  barWidthPx: number;
  markerPositionPx = 0;
  markerFromEndPx = 0;
  currentTimestamp: Date;
  isDragging = false;

  speed = 10;
  speeds = [5, 10, 20];
  playbackStatus: PlaybackStatus = PlaybackStatus.STOPPED;

  timeAxisMarkers: TimeAxisMarker[] = [];

  get markerLabelLeft(): string | null {
    // at the end of timeline
    if (this.markerFromEndPx < 20) {
      return 'unset';
    }
    // at the start of timeline
    if (this.markerPositionPx < 20) {
      return -this.markerPositionPx + 'px';
    }
    // otherwise default value from class timeline-marker-label
    return null;
  }

  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: number;
  private readonly RESIZE_DEBOUNCE_MS = 200;
  timeLineBarWidth: number;

  @ViewChild('timeLineBar') private timeLineBar: ElementRef<HTMLElement>;

  readonly PlaybackAction = PlaybackAction;
  readonly PlaybackStatus = PlaybackStatus;

  constructor(
    private readonly stormMapDataService: StormMapDataService,
  ) {
  }

  ngAfterViewInit() {
    // window resize or other parent component resize
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        if (newWidth !== this.timeLineBarWidth) {
          this.timeLineBarWidth = newWidth;

          // Debounce the update
          if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
          }

          this.resizeTimeout = window.setTimeout(() => {
            this.updateTimeMarkers(this.timeLineBarWidth, this.storm.startUnix, this.storm.endUnix);
          }, this.RESIZE_DEBOUNCE_MS);
        }
      }
    });

    this.resizeObserver.observe(this.timeLineBar.nativeElement);

    this.stormMapDataService.stormAnimationEndReached$.subscribe(() => {
      this.playbackStatus = PlaybackStatus.STOPPED;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const unixTimeChange = changes.time;
    if (unixTimeChange?.currentValue &&
      (unixTimeChange?.previousValue !== unixTimeChange.currentValue)
    ) {
      this.currentTimeChanged(unixTimeChange.currentValue);
    }
  }

  ngOnDestroy() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeObserver?.disconnect();
  }

  currentTimeChanged(unixtime: number) {
    this.currentTimestamp = new Date(unixtime * 1000);
    const startUnix = this.storm.startUnix;
    const endUnix = this.storm.endUnix;
    const durationSeconds = endUnix - startUnix;
    const barWidth = this.timeLineBar.nativeElement.getBoundingClientRect().width;
    if (durationSeconds > 0) {
      const percentage = Math.max(0, Math.min(100, ((unixtime - startUnix) / durationSeconds) * 100));
      this.markerPositionPx = barWidth * percentage / 100;
      this.markerFromEndPx = barWidth - this.markerPositionPx;
    } else {
      this.markerPositionPx = 0;
      this.markerFromEndPx = barWidth;
    }
    this.updateTimeMarkers(barWidth, startUnix, endUnix);
  }

  onTimeLineClick(event: MouseEvent): void {
    // Get the bounding rectangle of the bar
    const rect = this.timeLineBar.nativeElement.getBoundingClientRect();

    // Calculate click position relative to the bar
    this.markerPositionPx = event.clientX - rect.left;
    this.barWidthPx = rect.width;
    this.markerFromEndPx = this.barWidthPx - this.markerPositionPx;

    // Calculate percentage (ensure it's between 0 and 100)
    const current = this.getCurrentMomentBasedOnMarkerPosition();
    this.currentTimestamp = current.toDate();

    // Stop playback
    if (this.playbackStatus === PlaybackStatus.PLAYING) {
      this.actionTriggered(PlaybackAction.STOP);
    }
    this.timeChanged.emit(current.unix());
  }

  private getCurrentMomentBasedOnMarkerPosition(): moment.Moment {
    const percentage = Math.max(0, Math.min(100, (this.markerPositionPx / this.barWidthPx) * 100));

    const shiftStartMoment = moment(this.storm.startUnix * 1000);
    const numberOfSecondsSinceShiftStart = (moment(this.storm.endUnix * 1000).diff(shiftStartMoment, 'second') * percentage) / 100;
    return shiftStartMoment.clone().add(numberOfSecondsSinceShiftStart, 'seconds');
  }

  onMarkerMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;

    // add global mouse event listeners
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (!this.isDragging) {
      return;
    }

    const rect = this.timeLineBar.nativeElement.getBoundingClientRect();
    this.barWidthPx = rect.width;
    let newPosition = event.clientX - rect.left;
    newPosition = Math.max(0, Math.min(newPosition, this.barWidthPx));
    this.markerPositionPx = newPosition;
    this.markerFromEndPx = this.barWidthPx - newPosition;

    // calculate timestamp from percentage
    this.currentTimestamp = this.getCurrentMomentBasedOnMarkerPosition().toDate();
  };

  private onMouseUp = (): void => {
    if (!this.isDragging) {
      return;
    }

    const wasDragging = this.isDragging;
    this.isDragging = false;

    // remove global listeners
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);

    // emit the final timestamp
    if (wasDragging) {
      this.timeChanged.emit(this.getCurrentMomentBasedOnMarkerPosition().unix());
    }
  };

  // Parse time string to minutes since midnight
  timeToMinutes(time: Moment): number {
    const [hours, minutes] = [time.hours(), time.minutes()];
    return hours * 60 + minutes;
  }

  // Convert minutes since midnight to time string
  minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  // Calculate interval in minutes based on duration and width
  getOptimalInterval(durationMinutes: number, width: number): number {
    const minSpacing = 80; // minimum pixels between markers
    const maxMarkers = Math.floor(width / minSpacing);

    // Possible intervals in minutes
    const intervals = [5, 10, 15, 20, 30, 60, 120, 180, 360, 720, 1440];

    for (const interval of intervals) {
      const numMarkers = Math.ceil(durationMinutes / interval);
      if (numMarkers <= maxMarkers) {
        return interval;
      }
    }

    return intervals[intervals.length - 1];
  }

  updateTimeMarkers(width: number, startTime: number, endTime: number): void {
    const startTimeMoment = moment(startTime * 1000);
    const endTimeMoment = moment(endTime * 1000);
    const start = this.timeToMinutes(startTimeMoment);
    const end = this.timeToMinutes(endTimeMoment);
    const duration = end > start ? end - start : 1440 - start + end;

    const interval = this.getOptimalInterval(duration, width);
    const markers: TimeAxisMarker[] = [];

    // Add start marker
    markers.push({
      position: 0,
      label: startTimeMoment.format('HH:mma'),
      isStart: true,
    });

    // Round start to nearest interval for intermediate markers
    const firstMarker = Math.ceil(start / interval) * interval;

    // Add intermediate markers
    for (let time = firstMarker; time < start + duration; time += interval) {
      const position = ((time - start) / duration) * 100;
      if (position > 0 && position < 100) {
        markers.push({
          position,
          label: this.minutesToTime(time),
        });
      }
    }

    // Add end marker
    markers.push({
      position: 100,
      label: endTimeMoment.format('HH:mma'),
      isEnd: true,
    });

    this.timeAxisMarkers = markers;
  }

  onSpeedChanged(speed: number) {
    this.speed = speed;
    this.speedChanged.emit(speed);
  }

  actionTriggered(action: PlaybackAction) {
    switch (action) {
      case PlaybackAction.PLAY:
        this.playbackStatus = PlaybackStatus.LOADING;
        console.log('starting animation');
        this.playbackStatus = PlaybackStatus.PLAYING;
        // initial playback start
        if (!this.time) {
          this.time = this.storm.startUnix;
        }
        this.stormMapContent.filterTo(this.time);
        this.stormMapContent.playAnimation();
        break;
      default:
        this.playbackStatus = PlaybackStatus.STOPPED;
        console.log('stopped animation');
        this.stormMapContent.stopAnimation();
    }
  }
}
